import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Typography } from '@mui/material';

import { CancelButton } from 'src/components/common/buttons/CancelButton';
import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { PasswordVisibilityIcon } from 'src/features/auth/components/PasswordVisibilityIcon';
import {
	IChangePasswordFormData,
	IChangePasswordFormProps,
} from 'src/features/profile/domain/types';
import { ChangePasswordValidationSchema } from 'src/features/profile/domain/validation';
import { USER_PROFILE_LOADING_KEY } from 'src/features/profile/store/userProfileThunks';
import { changePassword } from 'src/features/profile/store/userProfileThunks';
import { AppDispatch } from 'src/store';

const ChangePasswordForm: React.FC<IChangePasswordFormProps> = ({
	overRideLoading = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[USER_PROFILE_LOADING_KEY],
	);
	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		resetField,
	} = useForm<IChangePasswordFormData>({
		resolver: yupResolver(ChangePasswordValidationSchema),
		defaultValues: {
			current_password: '',
			new_password: '',
			repeat_new_password: '',
		},
	});

	const [currentPasswordVisible, setCurrentPasswordVisible] =
		useState<boolean>(false);
	const [newPasswordVisible, setNewPasswordVisible] =
		useState<boolean>(false);
	const [repeatPasswordVisible, setRepeatPasswordVisible] =
		useState<boolean>(false);

	const handleChangePassword = async (data: IChangePasswordFormData) => {
		dispatch(
			changePassword({
				request: data,
				onSuccess: () => {
					reset();
					snackbar('Password changed successfully', 'success');
				},
				onError(err) {
					if (err.code === 1100) {
						resetField('current_password');
					}
				},
			}),
		);
	};

	const handleCancelPassword = async () => {
		reset();
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				padding: '24px',
				backgroundColor: 'white',
				borderRadius: '6px',
			}}
		>
			<Typography
				variant='h5'
				sx={{
					fontSize: '20px',
					marginBottom: '24px',
					textAlign: 'left',
				}}
			>
				Change Password
			</Typography>

			<form onSubmit={handleSubmit(handleChangePassword)}>
				<Controller
					name='current_password'
					control={control}
					render={({ field }) => (
						<TextField
							fullWidth
							label='Current Password'
							type={currentPasswordVisible ? 'text' : 'password'}
							error={!!errors?.current_password}
							helperText={errors?.current_password?.message}
							size='medium'
							sx={{ marginBottom: 2 }}
							InputProps={{
								endAdornment: (
									<PasswordVisibilityIcon
										handleToggle={() =>
											setCurrentPasswordVisible(
												(flag) => !flag,
											)
										}
										visibility={currentPasswordVisible}
									/>
								),
							}}
							{...field}
						/>
					)}
				/>

				<Controller
					name='new_password'
					control={control}
					render={({ field }) => (
						<TextField
							fullWidth
							label='New Password'
							type={newPasswordVisible ? 'text' : 'password'}
							error={!!errors?.new_password}
							helperText={errors?.new_password?.message}
							size='medium'
							sx={{ marginBottom: 2 }}
							InputProps={{
								endAdornment: (
									<PasswordVisibilityIcon
										handleToggle={() =>
											setNewPasswordVisible(
												(flag) => !flag,
											)
										}
										visibility={newPasswordVisible}
									/>
								),
							}}
							{...field}
						/>
					)}
				/>

				<Controller
					name='repeat_new_password'
					control={control}
					render={({ field }) => (
						<TextField
							fullWidth
							label='Repeat New Password'
							type={repeatPasswordVisible ? 'text' : 'password'}
							error={!!errors?.repeat_new_password}
							helperText={errors?.repeat_new_password?.message}
							size='medium'
							sx={{ marginBottom: 2 }}
							InputProps={{
								endAdornment: (
									<PasswordVisibilityIcon
										handleToggle={() =>
											setRepeatPasswordVisible(
												(flag) => !flag,
											)
										}
										visibility={repeatPasswordVisible}
									/>
								),
							}}
							{...field}
						/>
					)}
				/>

				<PrimaryButton
					isLoading={isLoading && !overRideLoading}
					sx={{ mb: '16px' }}
				>
					SAVE CHANGES
				</PrimaryButton>
				<CancelButton onClick={handleCancelPassword}>
					CANCEL
				</CancelButton>
			</form>
		</Box>
	);
};

export default ChangePasswordForm;
